import { Alert, AlertIcon, Box, Collapse, Flex, IconButton, VStack, useColorModeValue } from '@chakra-ui/react'
import React, { useRef } from 'react'

import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { useMessageFeedback } from '../../hooks/chat/useMessageFeedback'

import { KnowledgeSourceBadge } from '.'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { BotResponse } from '../../types/chatTypes'
import CopyButton from '../buttons/CopyButton'
import { AnswerError } from './AnswerError'
import { AnswerLoader } from './AnswerLoader'
import AnswerText from './AnswerText'
import FeedbackModal from './FeedbackModal'
import DisclaimerBox from './DisclaimerBox'
import { ProgressIndicator } from './ProgressIndicator'
import { SecondaryAnswerToggle } from './SecondaryAnswerToggle'
import ReferencesDisplay from './ReferencesDisplay'

interface Props {
  answer: BotResponse
  isLoading?: boolean
  withFeedback?: boolean
  onRetry: () => void
  onToggleMinimized?: () => void // Function to toggle minimized state
  hasMultipleAnswers?: boolean // Whether this thread has multiple answers
  // Enhanced dual answer UX props
  onPromoteToPrimary?: () => void // Function to promote this answer to primary
  onInternalRetry?: () => void // Function to retry internal search
  isProgressActive?: boolean // Whether search progress should be shown
  hasProgressError?: boolean // Whether search has an error
}

const AnswerComponent: React.FC<Props> = ({
  answer,
  withFeedback,
  isLoading,
  onRetry,
  onToggleMinimized,
  hasMultipleAnswers,
  onPromoteToPrimary,
  onInternalRetry,
  isProgressActive,
  hasProgressError,
}: Props) => {
  const { t } = useLanguage()
  const shouldUseFeedback = answer.needsFeedback !== false && withFeedback

  // Color mode values
  const iconColor = useColorModeValue('gray.400', 'gray.300')

  const focusRef = useRef<HTMLDivElement>(null)

  const {
    isLiked,
    isDisliked,
    isFeedbackModalOpen,
    likeMessage,
    dislikeMessage,
    closeFeedbackModal,
    submitFeedback,
  } = useMessageFeedback(shouldUseFeedback ? answer.message_id : '')

  // Check if the answer is empty
  const hasContent = !!answer.answer && answer.answer.trim() !== ''
  const isMinimized = answer.isMinimized === true
  const canToggle = onToggleMinimized && !answer.isPrimary && hasMultipleAnswers // Only allow toggling for non-primary responses when there are multiple answers

  // Note: isLLMMinimizedForInternal logic moved to SecondaryAnswerToggle component

  // Render if loading, has error, has content, OR is a minimized response in a dual setup
  const shouldRender = isLoading || answer.error || hasContent || (isMinimized && hasMultipleAnswers)

  // Enhanced toggle with analytics
  const handleToggleMinimized = () => {
    if (!onToggleMinimized) return

    onToggleMinimized()
  }

  // Handle promote to primary with analytics
  const handlePromoteToPrimary = () => {
    if (!onPromoteToPrimary) return

    onPromoteToPrimary()

    // Focus management for accessibility
    setTimeout(() => {
      focusRef.current?.focus()
    }, 100)
  }

  // Handle internal retry with analytics
  const handleInternalRetry = () => {
    if (!onInternalRetry) return

    onInternalRetry()
  }

  return (
    <>
      {/* Progress indicator for active searches */}
      {(isProgressActive || hasProgressError) && (
        <ProgressIndicator
          type={answer.knowledgeSource === 'INTERNAL_KB' ? 'internal' : 'external'}
          knowledgeSource={answer.knowledgeSource}
          isActive={isProgressActive || false}
          hasError={hasProgressError}
          onRetry={answer.knowledgeSource === 'INTERNAL_KB' ? handleInternalRetry : undefined}
        />
      )}

      {/* Only render if loading, has error, or has content */}
      {shouldRender && (
        <VStack align='stretch' spacing={2} width='100%' ref={focusRef} tabIndex={-1}>
          <KnowledgeSourceBadge
            knowledgeSource={answer.knowledgeSource || 'LLM_ONLY'}
            canMinimize={canToggle && !isMinimized}
            onMinimize={handleToggleMinimized}
          />
          {hasMultipleAnswers ? (
            <Collapse
              in={!isMinimized}
              animateOpacity
              transition={{ enter: { duration: 0.4 }, exit: { duration: 0.3 } }}
            >
              <Box width='100%'>
                {answer?.is_sensitive ? (
                  <Alert
                    status='warning'
                    color='orange.800'
                    borderRadius='md'
                    mb={2}
                    maxWidth={['80%', '80%', '70%']}
                  >
                    <AlertIcon />
                    {t('chat.sensitiveInformationWarning')}
                  </Alert>
                ) : null}
                {isLoading ? (
                    <AnswerLoader mt={1.5} knowledgeSource={answer.knowledgeSource} />
                  ) : answer.error && answer.error !== 'BOT_RESPONSE_CONTENT_VIOLATION' ? (
                    <AnswerError error={answer.error} onRetry={onRetry} errorDetails={answer.errorDetails} />
                  ) : null}
                  {/* Show violation details for BOT_RESPONSE_CONTENT_VIOLATION */}
                  {answer.error === 'BOT_RESPONSE_CONTENT_VIOLATION' && (
                    <Box mt={2}>
                      <AnswerError error={answer.error} onRetry={onRetry} errorDetails={answer.errorDetails} />
                    </Box>
                  )}
                  {/* Only show AnswerText if not loading and no error */}
                  {!isLoading && !answer.error && (
                    <AnswerText markdownContent={answer.answer} />
                  )}
                  {/* Show disclaimers if present */}
                  {!isLoading && !answer.error && answer.disclaimers && (
                    <DisclaimerBox disclaimers={answer.disclaimers} />
                  )}
              </Box>
            </Collapse>
          ) : (
            <Box width='100%'>
              {answer?.is_sensitive ? (
                <Alert status='warning' color='orange.800' borderRadius='md' mb={2} maxWidth={['80%', '80%', '70%']}>
                  <AlertIcon />
                  {t('chat.sensitiveInformationWarning')}
                </Alert>
              ) : null}
              {isLoading ? (
                <AnswerLoader mt={1.5} knowledgeSource={answer.knowledgeSource} />
              ) : answer.error && answer.error !== 'BOT_RESPONSE_CONTENT_VIOLATION' ? (
                <AnswerError error={answer.error} onRetry={onRetry} errorDetails={answer.errorDetails} />
              ) : null}
              {/* Show violation details for BOT_RESPONSE_CONTENT_VIOLATION */}
              {answer.error === 'BOT_RESPONSE_CONTENT_VIOLATION' && (
                <Box mt={2}>
                  <AnswerError error={answer.error} onRetry={onRetry} errorDetails={answer.errorDetails} />
                </Box>
              )}
                {/* Only show AnswerText if not loading and no error */}
                {!isLoading && !answer.error && (
                  <AnswerText markdownContent={answer.answer} />
                )}
                {/* Show disclaimers if present */}
                {!isLoading && !answer.error && answer.disclaimers && (
                  <DisclaimerBox disclaimers={answer.disclaimers} />
                )}
            </Box>
          )}

          {/* Show secondary answer toggle when collapsed */}
          {isMinimized && canToggle && (
            <SecondaryAnswerToggle
              answer={answer}
              isExpanded={false}
              onToggle={handleToggleMinimized}
              onPromoteToPrimary={onPromoteToPrimary ? handlePromoteToPrimary : undefined}
              isLoading={isLoading}
            />
          )}

          {/* Feedback buttons row - only show if content exists, feedback is enabled AND needsFeedback is not false AND not minimized */}
          {shouldUseFeedback && hasContent && !isLoading && !answer.error && !isMinimized && (
            <VStack align='stretch' spacing={2}>
              <Flex gap={2}>
                {/* Copy button */}
                <CopyButton textToCopy={answer.answer || ''} ariaLabel={t('common.copyAnswer')} />
                <IconButton
                  icon={<ThumbsUp size={18} />}
                  size='sm'
                  variant='ghost'
                  aria-label={t('feedback.likeAnswer')}
                  cursor='pointer'
                  onClick={likeMessage}
                  color={isLiked ? 'green.500' : iconColor}
                />
                <IconButton
                  icon={<ThumbsDown size={18} />}
                  size='sm'
                  variant='ghost'
                  aria-label={t('feedback.dislikeAnswer')}
                  cursor='pointer'
                  onClick={dislikeMessage}
                  color={isDisliked ? 'red.500' : iconColor}
                />
                <ReferencesDisplay references={answer.references} />
              </Flex>
            </VStack>
          )}
        </VStack>
      )}

      {shouldUseFeedback && (
        <FeedbackModal isOpen={isFeedbackModalOpen} onClose={closeFeedbackModal} onSubmit={submitFeedback} />
      )}
    </>
  )
}

AnswerComponent.displayName = 'Answer'

export const Answer = React.memo(AnswerComponent)
